import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Card } from "@shadcn-in-nx/ui/card";
import { Input } from "@shadcn-in-nx/ui/input";
import { BookOpen, ChevronDown, ChevronUp, Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import ResourceLibraryItem from "./ResourceLibraryItem";

const STORAGE_KEY = "ticket_library_open";
const TABS = [
    { id: "all", labelKey: "admin.support.libAll", labelDefault: "All" },
    { id: "doc", labelKey: "admin.support.libArticles", labelDefault: "Articles" },
    { id: "video", labelKey: "admin.support.libVideos", labelDefault: "Videos" },
];

export default function TicketResourceLibrary({ onInsert, customerLocale = "en" }) {
    const { t } = useTranslation();
    const [open, setOpen] = useState(() => localStorage.getItem(STORAGE_KEY) === "1");
    const [resources, setResources] = useState({ docs: [], videos: [] });
    const [search, setSearch] = useState("");
    const [tab, setTab] = useState("all");
    const [loaded, setLoaded] = useState(false);
    const searchRef = useRef(null);
    const { call: fetchResources, isLoading } = useBluelagoon({ method: "getSupportResources" });

    useEffect(() => {
        if (!open || loaded) return;
        fetchResources().then((r) => { if (r?.data) { setResources(r.data); setLoaded(true); } }).catch(() => {});
    }, [open, loaded, fetchResources]);

    useEffect(() => { if (open) requestAnimationFrame(() => searchRef.current?.focus()); }, [open]);

    const toggleOpen = useCallback(() => {
        setOpen((v) => { const n = !v; localStorage.setItem(STORAGE_KEY, n ? "1" : "0"); return n; });
    }, []);

    const items = useMemo(() => {
        const docs = (resources.docs || []).map((d) => ({ ...d, _type: "doc" }));
        const videos = (resources.videos || []).map((v) => ({ ...v, _type: "video" }));
        const all = tab === "doc" ? docs : tab === "video" ? videos : [...docs, ...videos];
        const q = search.trim().toLowerCase();
        if (!q) return all;
        return all.filter((r) =>
            r.title?.toLowerCase().includes(q) ||
            r.description?.toLowerCase().includes(q) ||
            r.tags?.some?.((tg) => tg.toLowerCase().includes(q)),
        );
    }, [resources, tab, search]);

    const counts = useMemo(() => ({ all: (resources.docs?.length || 0) + (resources.videos?.length || 0), doc: resources.docs?.length || 0, video: resources.videos?.length || 0 }), [resources]);

    const handleInsert = useCallback((it) => {
        if (!onInsert) return;
        onInsert(it.title, it.url);
        toast.success(t("admin.support.resourceInserted", "Inserted: {{title}}", { title: it.title }));
    }, [onInsert, t]);

    return (
        <Card className="overflow-hidden">
            <button onClick={toggleOpen} className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-accent/40 transition-colors text-left">
                <div className="flex items-center gap-2 min-w-0">
                    <BookOpen className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    <span className="font-medium text-sm">{t("admin.support.resourceLibrary", "Resource Library")}</span>
                    {loaded && <span className="text-xs text-muted-foreground">{counts.all} {t("admin.support.items", "items")}</span>}
                    <span className="text-[10px] font-mono uppercase tracking-wide px-1.5 py-0.5 rounded border border-amber-300 bg-amber-50 text-amber-700"
                        title={t("admin.support.customerLocaleTitle", "Articles default to the customer's language when available")}>
                        {customerLocale}
                    </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    {!open && <span className="hidden sm:inline">{t("admin.support.libHint", "Search articles & videos to insert into reply")}</span>}
                    {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
            </button>
            {open && (
                <div className="border-t p-3 sm:p-4 space-y-3 bg-muted/20">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input ref={searchRef} value={search} onChange={(e) => setSearch(e.target.value)}
                            placeholder={t("admin.support.libSearchPlaceholder", "Search by title, description, or tag...")}
                            className="pl-9" />
                    </div>
                    <div className="flex items-center gap-1 text-xs">
                        {TABS.map((tb) => (
                            <button key={tb.id} onClick={() => setTab(tb.id)}
                                className={`px-2.5 py-1 rounded-md transition-colors ${tab === tb.id ? "bg-blue-500 text-white" : "bg-white border hover:bg-accent"}`}>
                                {t(tb.labelKey, tb.labelDefault)} <span className="opacity-70">({counts[tb.id]})</span>
                            </button>
                        ))}
                    </div>
                    {isLoading && !loaded ? (
                        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-8 text-sm text-muted-foreground">{t("admin.support.noLibResults", "Nothing matches your search")}</div>
                    ) : (
                        <ul className="max-h-80 overflow-y-auto space-y-1.5 pr-1">
                            {items.map((it) => (
                                <ResourceLibraryItem key={`${it._type}-${it.id}`} item={it} onInsert={handleInsert} customerLocale={customerLocale} />
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </Card>
    );
}
